import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { PessoaDTO } from '../../../../../../models/clientes/pessoa.model';
import { PessoaService } from '../../../../../../services/pessoas/pessoas.service';
import { CidadeService } from '../../../../../../services/configuracoes/cidade.service';
import { PaisDDI } from '../../../../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../../../../../shared/data/paises-ddi.data';
import { TipoPessoa } from '../../../../../../enums/tipoPessoa.enum';

/**
 * O cadastro como o BACKEND devolve (`PessoaConsultaDTO`, PessoaController.kt:75,188):
 * `cidade` e' OBJETO (nao `cidade_id`) e `clientePadrao` existe — dois campos que o
 * model `PessoaDTO` do frontend nao declara. Alias LOCAL, sem tocar no model global.
 *
 * Nao usar `PessoaFormDTO`: sao 20+ campos que este dialogo NAO edita, e carrega-lo
 * sugeriria que o dialogo reconstroi o cadastro inteiro — exatamente a leitura errada
 * que um PUT REPLACE-TOTAL pune.
 */
export type PessoaCadastro = PessoaDTO & {
  cidade?: { id?: number; nome?: string } | null;
  clientePadrao?: boolean | null;
};

/** Modelo do formulario: SO o que este dialogo edita. */
interface CompletarClienteForm {
  ruc: string;
  ci: string;
  ddi: PaisDDI;
  /** Digitos puros, sem mascara (o app-phone-input emite assim). */
  telefone: string;
  cidadeId: number | null;
}

/** Opcao do dropdown de cidade ("Assuncao - Central"). */
interface CidadeOption {
  id: number;
  displayLabel: string;
  nome: string;
  nomeEstado: string | null;
}

/**
 * Dialogo "Completar cadastro do cliente" da aba "Nova venda" (Area do Contador).
 *
 * Grava RUC/CI, telefone e cidade de um cliente JA EXISTENTE, sem tirar o contador
 * da venda que ele esta montando. E' aberto pelo pai em dois momentos: ao SELECIONAR
 * um cliente incompleto e ao tentar FINALIZAR a venda.
 *
 * Filho burro de proposito: NAO conhece `comprador`, NAO conhece a venda e NAO sabe
 * de qual momento veio. Recebe `pessoaId` (escalar) e devolve, pelo @Output, o
 * cadastro que o SERVIDOR gravou. E' isso que torna a invariante do escritor unico
 * do pai (`atualizarValidacaoComprador()`) estrutural, e nao apenas disciplinar.
 *
 * SEM `providers: [MessageService]`. O `cotacao-dialog` declara o seu e por isso
 * precisa de um `<p-toast>` proprio; aqui usamos o MessageService da aplicacao, cujo
 * `<p-toast>` ja existe em `operar-cliente.component.html` — mesmo criterio do irmao
 * `operar-venda-item-selector`. Declarar um provider local aqui deixaria o toast
 * INVISIVEL.
 */
@Component({
  selector: 'app-operar-venda-completar-cliente',
  templateUrl: './operar-venda-completar-cliente.component.html',
  styleUrls: ['./operar-venda-completar-cliente.component.scss'],
})
export class OperarVendaCompletarClienteComponent implements OnChanges {
  /** Raiz das chaves i18n deste dialogo. */
  private static readonly I18N = 'contador_operar.venda.completar_cliente.';

  /**
   * Chaves do baseline que NUNCA entram no payload do PUT.
   * - `cidade`: o backend devolve OBJETO, mas so aceita `cidade_id` (PessoaDTO.kt:54).
   * - `estadoNome`/`cidadeNome`: campos fantasma do model do FE
   *   (models/clientes/pessoa.model.ts:20-21), inexistentes no backend.
   */
  private static readonly CHAVES_NAO_ENVIADAS = new Set(['cidade', 'estadoNome', 'cidadeNome']);

  @Input() visivel = false;
  @Input() pessoaId: number | null = null;
  /** Chaves i18n dos campos faltantes — SO para o destaque visual. */
  @Input() camposFaltantes: string[] = [];
  @Input() nomeCliente = '';

  @Output() visivelChange = new EventEmitter<boolean>();
  @Output() clienteAtualizado = new EventEmitter<PessoaCadastro>();

  /** Corpo do dialogo — usado so para levar o foco ao primeiro campo faltante. */
  @ViewChild('corpoDialogo') corpoDialogo?: ElementRef<HTMLElement>;

  // --- Estado ---
  carregando = false;
  salvando = false;
  cidadeCarregando = false;
  erroBaseline = false;
  erroCidades = false;
  /** Marca o primeiro clique em "Salvar" — libera as mensagens inline (DD-09). */
  tentouSalvar = false;

  /**
   * Baseline lido do servidor na abertura. `null` = NAO PODE GRAVAR.
   *
   * O `PUT /pessoas/{id}` e' REPLACE TOTAL (PessoaService.kt:117): sem o cadastro
   * completo em maos, gravar apagaria tudo o que nao estivesse no payload. O objeto
   * SINTETICO `{id, nome}` que a aba usa na pre-selecao do "Consumidor"
   * (operar-venda.component.ts:255-258) NAO serve de base — por isso relemos sempre.
   */
  private baseline: PessoaCadastro | null = null;

  /**
   * Tipo do cliente, lido do baseline. Decide QUAL documento aparece — RUC (PJ) ou
   * CI (PF) —, do mesmo jeito que a aba Clientes (clientes.component.html:52,74).
   *
   * `null` = nao sabemos. Nao e' um estado de erro: e' o gatilho do fallback que
   * mostra os DOIS campos (ver `mostrarRuc` / `mostrarCi`).
   *
   * Este dialogo apenas RESPEITA o tipo; nao ha seletor para troca-lo. Virar
   * JURIDICA tornaria `fantasia` obrigatoria no backend
   * (PessoaService.kt:122-126) e o campo nao existe aqui — o PUT voltaria 400 sem
   * saida para o usuario.
   */
  tipoPessoa: TipoPessoa | null = null;

  form: CompletarClienteForm = OperarVendaCompletarClienteComponent.formVazio();
  cidadeOptions: CidadeOption[] = [];

  // Mensagens inline por campo: o toast global diz "o que o servidor achou", o
  // inline diz "QUAL campo". Nao sao redundantes.
  erroRuc = '';
  erroCi = '';
  erroTelefone = '';
  erroCidade = '';
  erroGeral = '';

  constructor(
    private pessoaService: PessoaService,
    private cidadeService: CidadeService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  // ===========================================================================
  // Abertura
  // ===========================================================================

  ngOnChanges(changes: SimpleChanges): void {
    const mudouVisivel = changes['visivel'];
    if (mudouVisivel && this.visivel && mudouVisivel.previousValue !== true) {
      this.abrir();
    }
  }

  /**
   * Estado zerado + as duas cargas EM PARALELO (o custo e' o `max()`, nao a soma).
   * O baseline e' relido a CADA abertura: e' a garantia de nunca gravarmos sobre
   * dado velho.
   */
  private abrir(): void {
    this.tentouSalvar = false;
    this.salvando = false;
    this.baseline = null;
    // Zerado junto com o baseline: enquanto o GET nao volta, "nao sabemos" o tipo.
    this.tipoPessoa = null;
    this.erroBaseline = false;
    this.limparErros();
    this.form = OperarVendaCompletarClienteComponent.formVazio();

    this.carregarCidades();
    this.carregarBaseline();
  }

  private carregarBaseline(): void {
    const id = this.pessoaId;
    if (!id) {
      // Sem alvo nao ha o que ler nem o que gravar.
      this.erroBaseline = true;
      return;
    }

    this.carregando = true;
    // ATENCAO: o endpoint devolve PessoaConsultaDTO (cidade OBJETO), apesar de
    // `getById()` estar tipado `Observable<PessoaDTO>`.
    this.pessoaService.getById('/pessoas', String(id)).subscribe({
      next: (pessoa) => {
        this.carregando = false;
        // Guarda de corrida: o dialogo pode ter sido fechado ou apontado para
        // outro cliente enquanto a resposta estava em voo.
        if (!this.visivel || this.pessoaId !== id) return;

        const cadastro = pessoa as PessoaCadastro | null;
        if (!cadastro?.id) {
          this.erroBaseline = true;
          return;
        }
        this.baseline = cadastro;
        this.preencherFormulario(cadastro);
        this.focarPrimeiroFaltante();
      },
      error: () => {
        this.carregando = false;
        // 403/404 em GET sao SILENCIADOS pelo interceptor global
        // (http-error.interceptor.ts:39,63): sem este estado a tela ficaria muda.
        this.erroBaseline = true;
      },
    });
  }

  /**
   * Lista de cidades: uma vez por INSTANCIA do componente (sao ~397 itens e o dado
   * e' global). Reabrir o dialogo nao refaz este GET; o baseline, sim.
   */
  private carregarCidades(): void {
    if (this.cidadeOptions.length > 0) {
      this.erroCidades = false;
      return;
    }

    this.cidadeCarregando = true;
    this.erroCidades = false;
    this.cidadeService.get('/cidade?listarTodos=true').subscribe({
      next: (resposta) => {
        this.cidadeCarregando = false;
        this.cidadeOptions = (resposta?.content ?? []).map((c) => ({
          id: c.id as number,
          nome: c.nome,
          nomeEstado: c.nomeEstado ?? null,
          displayLabel: c.nomeEstado ? `${c.nome} - ${c.nomeEstado}` : c.nome,
        }));
        // Lista vazia tem, para o usuario, o mesmo efeito de nao ter carregado.
        this.erroCidades = this.cidadeOptions.length === 0;
      },
      error: () => {
        this.cidadeCarregando = false;
        this.erroCidades = true;
      },
    });
  }

  private preencherFormulario(base: PessoaCadastro): void {
    this.tipoPessoa = OperarVendaCompletarClienteComponent.lerTipoPessoa(base);
    this.form = {
      ruc: base.ruc ?? '',
      ci: base.ci ?? '',
      ddi: OperarVendaCompletarClienteComponent.acharPais(base.ddi),
      telefone: (base.telefone ?? '').replace(/\D/g, ''),
      // `cidade` (objeto) e' o que o GET devolve; `cidade_id` fica como
      // seguro-morto para uma futura troca de DTO.
      cidadeId: base.cidade?.id ?? base.cidade_id ?? null,
    };
  }

  // ===========================================================================
  // Validacao local (DD-09: o botao e' CLICAVEL; quem barra e' o clique)
  // ===========================================================================

  /**
   * RUC e CI continuam ALTERNATIVOS: basta um dos dois.
   *
   * Esta regra NAO acompanha a mudanca visual, de proposito. O backend aceita RUC
   * OU CI (`verificarDadosDaPessoaFaturaLegal`), e o hml tem 257 pessoas FISICA
   * com RUC preenchido e sem CI. Exigir o documento do TIPO barraria todas elas —
   * inclusive quando o dialogo foi aberto so por causa do telefone. So a
   * APRESENTACAO muda; o que conta como "preenchido", nao.
   */
  get grupoRucCiOk(): boolean {
    return !!(this.form.ruc.trim() || this.form.ci.trim());
  }

  /**
   * Formato do CI (clientes.component.ts:727-732): so digitos, de 5 a 8.
   *
   * So vale para o campo VISiVEL. Um CI-lixo herdado num cadastro JURIDICA fica
   * escondido e NAO trava a gravacao — o usuario nao teria como corrigi-lo daqui.
   */
  get ciFormatoOk(): boolean {
    if (!this.mostrarCi) return true;
    const ci = this.form.ci.trim();
    if (!ci) return true;
    return /^\d+$/.test(ci) && ci.length >= 5 && ci.length <= 8;
  }

  /**
   * Formato do RUC (clientes.component.ts:710-714): digitos e, opcionalmente,
   * hifen + digito verificador. Mesma regra do CI quanto a visibilidade.
   *
   * O CHECKSUM continua sendo do backend (`StringUtils.ehRucValido` →
   * 422 → `erroRuc`); aqui so barramos o que nem chega perto do formato.
   */
  get rucFormatoOk(): boolean {
    if (!this.mostrarRuc) return true;
    const ruc = this.form.ruc.trim();
    if (!ruc) return true;
    return /^\d+(-\d+)?$/.test(ruc);
  }

  get grupoTelefoneOk(): boolean {
    const digitos = this.telefoneDigitos();
    // 9-20 e' o @Pattern do backend (PessoaDTO.kt:49-50).
    return digitos.length >= 9 && digitos.length <= 20;
  }

  get grupoCidadeOk(): boolean {
    return this.form.cidadeId != null;
  }

  /**
   * O botao "Salvar" fica desabilitado em DOIS casos, e so nesses: sem baseline
   * (nao ha o que gravar) e durante o salvamento. Com o formulario incompleto ele
   * continua CLICAVEL — um botao cinza sem dizer o porque nao ensina o usuario
   * leigo qual dos tres grupos travou.
   */
  get podeGravar(): boolean {
    return !!this.baseline && !this.salvando;
  }

  get formularioValido(): boolean {
    return (
      this.grupoRucCiOk &&
      this.ciFormatoOk &&
      this.rucFormatoOk &&
      this.grupoTelefoneOk &&
      this.grupoCidadeOk
    );
  }

  // ===========================================================================
  // Qual documento aparece (espelha a aba Clientes)
  // ===========================================================================

  /**
   * PJ ve RUC; PF nao. Tipo DESCONHECIDO ve os dois — REGRA DE OURO: na duvida,
   * NAO bloquear. Esconder os dois campos deixaria o contador sem como completar o
   * cadastro e travaria a venda inteira.
   */
  get mostrarRuc(): boolean {
    return this.tipoPessoa !== TipoPessoa.FISICA;
  }

  /** PF ve CI; PJ nao. Tipo desconhecido ve os dois (mesmo motivo de `mostrarRuc`). */
  get mostrarCi(): boolean {
    return this.tipoPessoa !== TipoPessoa.JURIDICA;
  }

  /**
   * Titulo do grupo: "RUC", "CI" ou, no fallback, o "RUC ou CI" de sempre.
   * Com UM campo so, este titulo passa a ser o rotulo — por isso reaproveita as
   * chaves `label_ruc`/`label_ci` em vez de criar textos paralelos que poderiam
   * divergir na traducao.
   */
  get chaveTituloRucCi(): string {
    return this.chaveRucCi('label_ruc', 'label_ci', 'grupo_ruc_ci');
  }

  /** Ajuda do grupo: o formato esperado ou, no fallback, "Preencha um dos dois.". */
  get chaveAjudaRucCi(): string {
    return this.chaveRucCi('grupo_ruc_ajuda', 'grupo_ci_ajuda', 'grupo_ruc_ci_ajuda');
  }

  /** Mensagem de campo vazio, com o nome do documento que o cliente realmente usa. */
  get chaveObrigatorioRucCi(): string {
    return this.chaveRucCi('obrigatorio_ruc', 'obrigatorio_ci', 'obrigatorio_ruc_ci');
  }

  /** Chave i18n COMPLETA conforme o tipo (juridica, fisica, desconhecido). */
  private chaveRucCi(juridica: string, fisica: string, ambos: string): string {
    const sufixo =
      this.tipoPessoa === TipoPessoa.JURIDICA
        ? juridica
        : this.tipoPessoa === TipoPessoa.FISICA
          ? fisica
          : ambos;
    return OperarVendaCompletarClienteComponent.I18N + sufixo;
  }

  // --- Sanitizacao da digitacao (identica a da aba Clientes) ---

  /** CI: so digitos, no maximo 8 (clientes.component.ts:735-740). */
  onCiModelChange(valor: string): void {
    const apenasDigitos = (valor || '').replace(/\D/g, '').substring(0, 8);
    if (valor !== apenasDigitos) {
      this.form.ci = apenasDigitos;
    }
  }

  /**
   * RUC: aceita `[0-9-]` (clientes.component.ts:717-724). NAO usar a diretiva
   * `digitsOnly` aqui — ela apagaria o hifen do digito verificador.
   */
  onRucInput(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const filtrado = (input.value || '').replace(/[^0-9-]/g, '');
    if (filtrado !== input.value) {
      input.value = filtrado;
      this.form.ruc = filtrado;
    }
  }

  // --- Destaque visual dos grupos que faltam no cadastro ---
  faltaRucCi(): boolean {
    return this.camposFaltantes.includes('contador_operar.venda.campo_ruc_ci');
  }

  faltaTelefone(): boolean {
    return this.camposFaltantes.includes('contador_operar.venda.campo_telefone');
  }

  faltaCidade(): boolean {
    return this.camposFaltantes.includes('contador_operar.venda.campo_cidade');
  }

  // ===========================================================================
  // Gravacao
  // ===========================================================================

  salvar(): void {
    this.tentouSalvar = true;
    this.limparErros();

    // Sem baseline ou ja salvando: o botao ja esta desabilitado, isto e' a rede.
    if (!this.podeGravar || !this.baseline) return;

    // DD-09: formulario incompleto NAO dispara PUT. So acendem as mensagens
    // inline, que `tentouSalvar` acabou de liberar.
    if (!this.formularioValido) return;

    const payload = this.montarPayload(this.baseline);
    // O alvo é o dono do BASELINE, não o `@Input() pessoaId` corrente. São sempre
    // o mesmo cliente na prática (o diálogo é modal), mas um PUT de replace total
    // endereçado ao id errado sobrescreveria o cadastro de OUTRA pessoa — e o
    // payload só é válido para quem o originou.
    const id = this.baseline.id;
    this.salvando = true;

    // `PessoaService.put` esta tipado `PessoaDTO`, que nao declara `clientePadrao`
    // nem `cidade`. O cast e' unico, localizado e documentado — nao usar `as any`.
    this.pessoaService.put(`/pessoas/${id}`, payload as unknown as PessoaDTO).subscribe({
      next: (resposta) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.t('sucesso'),
        });

        // O que vale e' o cadastro DEVOLVIDO pelo servidor, nunca o que foi
        // digitado. O `id` cai para `pessoaId` porque, sem ele, a guarda de
        // corrida do pai descartaria uma gravacao bem-sucedida.
        const devolvido = (resposta ?? {}) as PessoaCadastro;
        const atualizado: PessoaCadastro = { ...devolvido, id: devolvido.id ?? id };

        // Fecha ANTES de emitir: o pai pode encadear o dialogo de cotacao, e os
        // dois nunca podem coexistir na tela.
        this.fechar();
        this.clienteAtualizado.emit(atualizado);
      },
      error: (err: HttpErrorResponse) => {
        this.salvando = false;
        // O dialogo PERMANECE aberto e o que foi digitado PERMANECE na tela.
        this.tratarErroDeGravacao(err);
      },
    });
  }

  /**
   * Monta o corpo do PUT /pessoas/{id} por CoPIA das chaves do baseline lido do
   * servidor.
   *
   * NAO trocar por um literal com lista de campos: o PUT e' REPLACE TOTAL
   * (PessoaService.kt:117) e uma lista escrita a mao passa a APAGAR, em silencio,
   * qualquer campo que o backend venha a acrescentar. Isso ja se materializou no
   * projeto: `clientes.component.ts:526-544` monta o body a mao, omite
   * `clientePadrao` e o zera a cada edicao (achado A-08).
   */
  private montarPayload(baseline: PessoaCadastro): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    const Cls = OperarVendaCompletarClienteComponent;

    // --- Passo 1: copia generica -------------------------------------------
    for (const [chave, valor] of Object.entries(baseline)) {
      if (Cls.CHAVES_NAO_ENVIADAS.has(chave)) continue;

      // Chave nula/ausente e' OMITIDA: os defaults do Kotlin so valem com a chave
      // AUSENTE; `null` explicito em campo @NotNull volta 400 (PessoaDTO.kt:39,44,46).
      if (valor === null || valor === undefined) continue;

      // Enum vem como objeto {enum,nome,id,descricao} (LocalizableEnumSerializer.kt:34-46)
      // e so volta como STRING. Molde: clientes.component.ts:610-619.
      if (typeof valor === 'object' && 'enum' in (valor as Record<string, unknown>)) {
        payload[chave] = (valor as { enum: string }).enum;
        continue;
      }

      // Qualquer outro objeto nao sabemos reenviar: nao inventa, nao envia.
      //
      // SEGURO por um motivo VERIFICADO, nao por sorte: categoriaCliente,
      // categoriaFornecedor e categoriaVendedor sao Boolean, NAO objetos aninhados.
      // Se fossem objetos, este `continue` apagaria as tres categorias em silencio
      // a cada gravacao. Antes de acrescentar campo objeto ao DTO, reveja isto.
      if (typeof valor === 'object') continue;

      payload[chave] = valor;
    }

    // --- Passo 2: correcoes obrigatorias sobre a copia -----------------------
    // `fantasia` = "" viola @Length(min=3) (PessoaDTO.kt:27) e o proprio backend
    // FABRICA "" (PessoaFormMapper.kt:25). Sao 296 de 2770 cadastros do hml assim,
    // todos FISICA — entre eles o "Consumidor" id 2931, o caso central desta
    // feature. Ausente = null = aceito, e o mapper repoe "".
    //
    // ATENCAO: esta omissao e' 400 para tipoPessoa = JURIDICA —
    // verificarTipoPessoaJuridica() faz requireNotNull(fantasia)
    // (PessoaService.kt:115,122-126) — e enviar "" tambem e' 400 por @Length. Ou
    // seja, um PJ com fantasia curta e' insalvavel por este dialogo pelos dois
    // caminhos. Hoje o caminho e' INALCANCAVEL: 899 pessoas JURIDICA no hml, ZERO
    // com fantasia nula ou curta. NAO "resolver" inventando um valor (ex.: copiar
    // `nome`): escrever dado que o usuario nao digitou nao e' opcao. Se aparecer um
    // PJ assim, o caminho e' expor o campo (mudanca de escopo) ou corrigir o backend.
    if (String(payload['fantasia'] ?? '').trim().length < 3) {
      delete payload['fantasia'];
    }
    // `email` e' @field:NotNull no DOMiNIO (Pessoa.kt:28-30) e @Email aceita "".
    payload['email'] = baseline.email || '';

    // --- Passo 3: os campos que ESTE dialogo edita ---------------------------
    this.definirOuRemover(payload, 'ruc', this.form.ruc.trim() || null);
    this.definirOuRemover(payload, 'ci', this.form.ci.trim() || null);

    const telefone = this.telefoneDigitos();
    this.definirOuRemover(payload, 'telefone', telefone || null);
    // `ddi` so acompanha `telefone`: sozinho e' ruido, e sem ele o @AssertTrue
    // `isDdi()` (PessoaDTO.kt:88-91) devolve 400.
    this.definirOuRemover(payload, 'ddi', telefone ? this.ddiParaEnvio() : null);

    // `cidade_id`, NUNCA `cidade: {id}` — o quick-add
    // (pessoa-adicionar-dialog.component.ts:274) faz errado e a cidade nao persiste.
    this.definirOuRemover(payload, 'cidade_id', this.form.cidadeId ?? null);

    return payload;
  }

  /** Grava a chave quando ha valor; REMOVE quando nao ha (limpar campo = apagar). */
  private definirOuRemover(alvo: Record<string, unknown>, chave: string, valor: unknown): void {
    if (valor === null || valor === undefined || valor === '') {
      delete alvo[chave];
      return;
    }
    alvo[chave] = valor;
  }

  /**
   * Formato do DDI. O banco tem os DOIS formatos ("+595" e "595") porque as telas
   * divergem — `clientes.component.ts:532` envia com "+",
   * `pessoa-adicionar-dialog.component.ts:265` envia sem. Nenhum consumidor do
   * backend faz parsing do campo, entao isto e' cosmetico: PRESERVAMOS o formato
   * que o cadastro ja usava e, quando nao havia nenhum, seguimos a maioria (sem "+").
   *
   * `PaisDDI` NAO tem a propriedade `codigo`; a propriedade e' `ddi`, e ja vem com "+".
   */
  private ddiParaEnvio(): string {
    const codigo = this.form.ddi?.ddi || '+595';
    const baselineUsavaMais = (this.baseline?.ddi || '').startsWith('+');
    return baselineUsavaMais ? codigo : codigo.replace('+', '');
  }

  // ===========================================================================
  // Erros de gravacao
  // ===========================================================================

  private tratarErroDeGravacao(err: HttpErrorResponse): void {
    const corpo = err?.error as { message?: string; errors?: { campo?: string; mensagem?: string }[] };

    // 422 NAO e' sinonimo de RUC: o codigo ramifica por STATUS, e o 422 tem mais
    // de um produtor. `validaRuc` (PessoaService.kt:80-85,114) lanca
    // EDadoInvalidoException, mas `PessoaFormMapper.kt:18-21` lanca
    // NegocioException ("Cidade nao encontrada") para `cidade_id` inexistente, e
    // ExceptionHandler.kt:356-357 tambem a mapeia para 422. Enderecar tudo ao RUC
    // mostraria "RUC invalido" sob um campo intocado, sem saida para o usuario.
    //
    // O discriminador e' deterministico: `validaRuc` so dispara com RUC nao-vazio,
    // logo um 422 sem `ruc` no payload NAO pode ser do RUC. Nesses casos a
    // mensagem do backend e' o melhor texto disponivel. Em ambos os ramos o corpo
    // e' ErrorView<String>, SEM `errors[]`.
    if (err.status === 422) {
      const enviouRuc = (this.form.ruc ?? '').trim() !== '';
      if (enviouRuc) {
        this.erroRuc = this.t('ruc_invalido');
      } else {
        this.erroGeral = corpo?.message || this.t('erro_salvar');
      }
      return;
    }

    // 400 de bean validation e' o UNICO que traz o nome do campo.
    if (err.status === 400 && Array.isArray(corpo?.errors)) {
      let houveCampoForaDoDialogo = false;
      for (const e of corpo.errors ?? []) {
        switch (e?.campo) {
          case 'ruc':
            this.erroRuc = e.mensagem ?? '';
            break;
          case 'ci':
            this.erroCi = e.mensagem ?? '';
            break;
          case 'ddi':
          case 'telefone':
            this.erroTelefone = e.mensagem ?? '';
            break;
          case 'cidade':
          case 'cidade_id':
            // Tambem e' editavel aqui: mandar o usuario para a aba Clientes por
            // causa dele seria um desvio inutil.
            this.erroCidade = e.mensagem ?? '';
            break;
          default:
            houveCampoForaDoDialogo = true;
        }
      }
      // `nome`, `fantasia`, `email`: nao ha como corrigir daqui.
      if (houveCampoForaDoDialogo) this.erroGeral = this.t('erro_outro_campo');
      return;
    }

    // 403 e' SILENCIADO pelo interceptor global (STATUS_SILENCIOSOS,
    // http-error.interceptor.ts:39). Sem este ramo a tela fica MUDA.
    if (err.status === 403) {
      this.erroGeral = this.t('sem_permissao');
      return;
    }

    // Fallback. Cobre tambem o 400 SEM `errors[]` legitimo do PJ com `fantasia`
    // omitida (PessoaService.kt:122-126): a mensagem do backend e' o melhor texto
    // disponivel, e o campo nao e' editavel aqui.
    this.erroGeral = corpo?.message || this.t('erro_salvar');
  }

  // ===========================================================================
  // Fechamento e utilitarios
  // ===========================================================================

  /**
   * Idempotente de proposito: e' chamado pelo botao Cancelar E pelo `(onHide)` do
   * p-dialog (ESC). Reemitir `false` para o pai nao tem efeito colateral, e depender
   * so do `(onHide)` deixaria o fechamento preso a animacao do PrimeNG.
   */
  fechar(): void {
    this.visivel = false;
    this.visivelChange.emit(false);
  }

  private telefoneDigitos(): string {
    return (this.form.telefone || '').replace(/\D/g, '');
  }

  private limparErros(): void {
    this.erroRuc = '';
    this.erroCi = '';
    this.erroTelefone = '';
    this.erroCidade = '';
    this.erroGeral = '';
  }

  /** Traduz uma chave DESTE dialogo. */
  private t(chave: string, params?: object): string {
    return this.translate.instant(OperarVendaCompletarClienteComponent.I18N + chave, params);
  }

  /**
   * A11y: o foco vai para o primeiro campo QUE FALTA, nao para o primeiro campo do
   * formulario — e' o que o usuario veio resolver. O `setTimeout` e' necessario
   * porque os campos so entram no DOM quando `carregando` vira false.
   */
  private focarPrimeiroFaltante(): void {
    setTimeout(() => {
      // `#ccc-ruc` so existe quando o RUC esta visivel: com um cliente FISICA o
      // seletor fixo antigo nao acharia nada e o dialogo abriria SEM foco.
      const documento = this.mostrarRuc ? '#ccc-ruc' : '#ccc-ci';
      const seletor = this.faltaRucCi()
        ? documento
        : this.faltaTelefone()
          ? '.ccc-campo-telefone input'
          : this.faltaCidade()
            ? '#ccc-cidade'
            : documento;
      this.corpoDialogo?.nativeElement.querySelector<HTMLElement>(seletor)?.focus();
    });
  }

  private static formVazio(): CompletarClienteForm {
    return { ruc: '', ci: '', ddi: PAISES_DDI[0], telefone: '', cidadeId: null };
  }

  /**
   * Le o `tipoPessoa` do cadastro devolvido pelo GET.
   *
   * O backend serializa ENUM como OBJETO `{enum, nome, id, descricao}`
   * (LocalizableEnumSerializer.kt:34-48). Comparar o campo direto com 'FISICA'
   * NUNCA casa — o dialogo cairia sempre no fallback, em silencio. Mesmo molde de
   * `clientes.component.ts:610-611` (`?.enum || valor`), aqui com o fallback para
   * STRING porque nada garante o formato do objeto.
   *
   * Devolve `null` para nulo, ausente ou desconhecido. Nao inventamos um padrao
   * (ex.: FISICA): chutar esconderia o RUC de quem talvez so tenha RUC.
   */
  private static lerTipoPessoa(base: PessoaCadastro): TipoPessoa | null {
    const bruto: unknown = base.tipoPessoa;
    const valor =
      bruto && typeof bruto === 'object' ? (bruto as { enum?: unknown }).enum : bruto;
    const texto = String(valor ?? '').trim().toUpperCase();

    if (texto === TipoPessoa.FISICA) return TipoPessoa.FISICA;
    if (texto === TipoPessoa.JURIDICA) return TipoPessoa.JURIDICA;
    return null;
  }

  /** Casa o DDI gravado ("+595" ou "595") com a entrada da lista de paises. */
  private static acharPais(ddi?: string | null): PaisDDI {
    const alvo = String(ddi ?? '').replace(/\D/g, '');
    if (!alvo) return PAISES_DDI[0];
    return PAISES_DDI.find((p) => p.ddi.replace(/\D/g, '') === alvo) ?? PAISES_DDI[0];
  }
}
